import { useEffect, useRef, useState } from "react";
import { GripVertical } from "lucide-react";
import { GRID_W, GRID_H, CELL_FT, readAt } from "../../lib/sampleLake";
import { drawMap } from "../../lib/drawMap";

const fmtDepth = (ft, unit) => (unit === "m" ? `${(ft * 0.3048).toFixed(1)} m` : `${ft.toFixed(1)} ft`);

// Redraws the map whenever its inputs change or the canvas is resized.
const useMapDraw = (canvasRef, scene) => {
  const { layer, showTransects, points, hover, compare, split } = scene;
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth, h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawMap(ctx, w, h, { layer, showTransects, points, hover, compare, split });
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [canvasRef, layer, showTransects, points, hover, compare, split]);
};

const MapTooltip = ({ hover, unit, compare }) => (
  <div
    data-testid="sample-map-tooltip"
    className="pointer-events-none absolute -translate-x-1/2 -translate-y-[calc(100%+14px)] glass border border-cyan-500/30 rounded-lg px-2.5 py-1.5 text-xs whitespace-nowrap"
    style={{ left: `${(hover.gx / GRID_W) * 100}%`, top: `${(hover.gy / GRID_H) * 100}%` }}
  >
    {!hover.water ? (
      <span className="font-mono text-slate-300">Shoreline / land</span>
    ) : compare ? (
      <span className="font-mono text-cyan-200 font-semibold">{hover.vegetation.toFixed(0)}% → {hover.vegetation2025.toFixed(0)}%</span>
    ) : (
      <span className="font-mono text-cyan-200 font-semibold">{fmtDepth(hover.depth, unit)}</span>
    )}
  </div>
);

const CompareHandle = ({ split, canvasRef, setSplit }) => {
  const dragStart = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = canvasRef.current.getBoundingClientRect();
    const move = (ev) => setSplit(Math.min(0.97, Math.max(0.03, (ev.clientX - rect.left) / rect.width)));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div
      data-testid="sample-map-compare-handle"
      role="slider"
      aria-label="Compare 2024 and 2025 vegetation"
      aria-valuenow={Math.round(split * 100)}
      onPointerDown={dragStart}
      className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white text-slate-900 shadow-lg border-2 border-cyan-300 flex items-center justify-center cursor-ew-resize touch-none"
      style={{ left: `${split * 100}%` }}
    >
      <GripVertical className="w-4 h-4" />
    </div>
  );
};

const ScaleBar = ({ hidden }) => (
  <div
    className="pointer-events-none absolute right-3 bottom-3 text-[11px] font-mono text-slate-200"
    style={{ width: `${(1320 / CELL_FT / GRID_W) * 100}%`, display: hidden ? "none" : undefined }}
  >
    <div className="text-right mb-1 drop-shadow">0.25 mi</div>
    <div className="h-[3px] bg-slate-100 border-x-2 border-slate-100 shadow" />
  </div>
);

export const SampleMapCanvas = ({ layer, showTransects, unit, points, compare, split, setSplit, onHover, onPick }) => {
  const canvasRef = useRef(null);
  const [hover, setHover] = useState(null);
  useMapDraw(canvasRef, { layer, showTransects, points, hover, compare, split });

  const toGrid = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return readAt(((e.clientX - rect.left) / rect.width) * GRID_W, ((e.clientY - rect.top) / rect.height) * GRID_H);
  };
  const update = (r) => { setHover(r); onHover(r); };
  const handleDown = (e) => {
    const r = toGrid(e);
    update(r);
    if (r && r.water) onPick(r);
  };

  return (
    <div className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden border border-slate-700/60 shadow-[0_0_40px_rgba(0,240,255,0.08)] bg-[#131B2E]">
      <canvas
        ref={canvasRef}
        data-testid="sample-map-canvas"
        className="w-full h-full touch-none cursor-crosshair block"
        onPointerMove={(e) => update(toGrid(e))}
        onPointerLeave={() => update(null)}
        onPointerDown={handleDown}
      />
      {compare && <CompareHandle split={split} canvasRef={canvasRef} setSplit={setSplit} />}
      {hover && <MapTooltip hover={hover} unit={unit} compare={compare} />}
      <div className="pointer-events-none absolute left-3 top-3 hidden sm:block glass border border-slate-700/60 rounded-lg px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-300" data-testid="sample-map-label">
        Sample Survey: Clearwater Reservoir <span className="text-amber-300">(demo data)</span>
      </div>
      <ScaleBar hidden={compare} />
    </div>
  );
};
